import { inject, injectable } from 'inversify';

import { HttpService } from 'src/core/services/http.service';
import { container } from 'src/inversify.config';
import { ContextService } from 'src/core/services/context.service';
import { PolicyCheckingService } from 'src/app/bookings/shared/policy-checking.service';
import { QuoteDataService } from 'src/app/bookings/quotes/form/quote-form.data';
import { MarketListDataService } from 'src/app/general/market/list/market-list.data';
import { TaxListDataService } from 'src/app/general/taxes/list/tax-list.data';
import { QuoteEntryDataService } from 'src/app/bookings/quote-entries/quote-entry-form.data';

@injectable()
export class BillingService {
	constructor(
		@inject(HttpService)
		private httpService: HttpService
	) { }

	async adjustQuoteEntryPrice(saleAdjustment: SalesAdjustmentProductManually): Promise<void> {
		const contextService = container.get(ContextService);
		const policyCheckingService = container.get(PolicyCheckingService);

		const quoteEntryDataService = QuoteEntryDataService.make(policyCheckingService, contextService);
		const quoteDataService = QuoteDataService.make(contextService);
		const marketListDataService = MarketListDataService.make(contextService);
		const taxListDataService = TaxListDataService.make(contextService);

		quoteDataService.addFilter('id', 'eq', saleAdjustment.quoteId);
		quoteDataService.addSorting('id', 'asc', true);
		const [quote] = await quoteDataService.load(query => query);

		marketListDataService.addFilter('id', 'eq', quote.marketId);
		marketListDataService.addSorting('id', 'asc', true);
		const [market] = await marketListDataService.load(query => query);

		taxListDataService.addFilter('isDeleted', 'eq', false);
		taxListDataService.addSorting('id', 'asc', true);
		const taxes = await taxListDataService.load(query => query);

		quoteEntryDataService.addFilter('quoteId', 'eq', saleAdjustment.quoteId);
		quoteEntryDataService.addFilter('isDeleted', 'eq', false);
		quoteEntryDataService.addSorting('id', 'asc', true);
		quoteEntryDataService.pageSize = 100;
		const quoteEntries = (await quoteEntryDataService.load(query => query)).slice();

		const depositPercent = market.requiresPayment ? market.depositPercent : 0;
		const dueDateInDays = market.invoiceDueDateInDays;

		await quoteEntryDataService.adjustEntryPrice(quote, quoteEntries, saleAdjustment, (entries) => {
			quoteDataService.updateQuote(quote, depositPercent, dueDateInDays, taxes, entries, false);
		});
		await quoteEntryDataService.save();
	}

	/**
	 * Generate Invoices for a quote
	 * @param quoteId
	 */
	async generateInvoices(quoteId: number): Promise<InvoiceSummary[]> {
		const url = `api/v1/invoices/quote?quoteId=${quoteId}`;

		const response = await this.httpService.post(url, {});
		return response.data;
	}

	/**
	 * Generate invoices for a supplier
	 * @param supplierId
	 * @param invoiceDate
	 * @param invoiceReferenceNumber
	 * @param payloads
	 */
	async registerSupplierInvoices(supplierId: number, invoiceDate: Date, invoiceReferenceNumber: string, payloads: InvoiceEntryPayload[]): Promise<void> {
		const url = 'api/v1/invoices/supplier';

		await this.httpService.post(url, {
			supplierId,
			invoiceDate,
			invoiceReferenceNumber,
			payloads
		});
	}

	async updateInvoiceEntry(invoiceEntryId: number, payload: InvoiceEntryPayload): Promise<void> {
		const url = 'api/v1/invoices/update';

		await this.httpService.post(url, {
			invoiceEntryId,
			payload
		});
	}

	async changeState(invoiceId: number): Promise<void> {
		const url = `api/v1/invoices/change-state?invoiceId=${invoiceId}`;

		await this.httpService.post(url, {});
	}

	async generateCreditNote(invoiceId: number): Promise<void> {
		const url = `api/v1/invoices/credit-note?invoiceId=${invoiceId}`;

		await this.httpService.post(url, {});
	}

	async registerPayment(payloads: InvoicePaymentPayload[], paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean): Promise<PaymentSummary> {
		const url = 'api/v1/payments/register-payment';

		const response = await this.httpService.post(url, {
			payloads,
			paymentInfo,
			immediatelyPerformPayment
		});
		return response.data;
	}

	async registerRefund(paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean, targetPaymentId?: number): Promise<PaymentSummary> {
		const url = 'api/v1/payments/register-refund';

		const response = await this.httpService.post(url, {
			paymentInfo,
			immediatelyPerformPayment,
			targetPaymentId
		});
		return response.data;
	}

	async performPayment(paymentId: number): Promise<void> {
		const url = `api/v1/payments/perform-payment?paymentId=${paymentId}`;

		await this.httpService.post(url, {});
	}

	async getBillingAddresses(target: number, id: number): Promise<Address[]> {
		const url = `api/v1/payments/get-billing-addresses/${target}/${id}`;

		const result = await this.httpService.get(url);
		return result.data.map(e => ({
			address1: e.address1,
			address2: e.address2,
			town: e.town,
			county: e.state,
			postalCode: e.postalCode,
			country: e.country,
		}));
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/shared/billing.service.ts